export type FunctionCategory = 'math' | 'text' | 'date' | 'logical' | 'statistical' | 'financial' | 'lookup' | 'engineering' | 'information' | 'matrix';

export const categoryLabels: Record<FunctionCategory, { pt: string; fr: string }> = {
    math: { pt: 'Matemática', fr: 'Mathématiques' },
    text: { pt: 'Texto', fr: 'Texte' },
    date: { pt: 'Data e Hora', fr: 'Date et Heure' },
    logical: { pt: 'Lógica', fr: 'Logique' },
    statistical: { pt: 'Estatística', fr: 'Statistique' },
    financial: { pt: 'Financeira', fr: 'Finance' },
    lookup: { pt: 'Pesquisa e Referência', fr: 'Recherche et Référence' },
    engineering: { pt: 'Engenharia', fr: 'Ingénierie' },
    information: { pt: 'Informação', fr: 'Information' },
    matrix: { pt: 'Matriz', fr: 'Matrice' },
};

export const functionDescriptions: Record<string, { syntax: string; description: { pt: string; fr: string }; category: FunctionCategory }> = {
    // ==================== MATH ====================
    ABS: {
        syntax: '(num)',
        description: { pt: 'Devolve o valor absoluto de um número', fr: 'Renvoie la valeur absolue d\'un nombre' },
        category: 'math',
    },
    ACOS: {
        syntax: '(num)',
        description: { pt: 'Devolve o arco cosseno de um número', fr: 'Renvoie l\'arc cosinus d\'un nombre' },
        category: 'math',
    },
    ACOSH: {
        syntax: '(num)',
        description: { pt: 'Devolve o cosseno hiperbólico inverso', fr: 'Renvoie le cosinus hyperbolique inverse' },
        category: 'math',
    },
    ACOT: {
        syntax: '(num)',
        description: { pt: 'Devolve o arco cotangente de um número', fr: 'Renvoie l\'arc cotangente d\'un nombre' },
        category: 'math',
    },
    ACOTH: {
        syntax: '(num)',
        description: { pt: 'Devolve a cotangente hiperbólica inversa', fr: 'Renvoie la cotangente hyperbolique inverse' },
        category: 'math',
    },
    ARABIC: {
        syntax: '(texto)',
        description: { pt: 'Converte numeral romano em número arábico', fr: 'Convertit un chiffre romain en nombre arabe' },
        category: 'math',
    },
    ASIN: {
        syntax: '(num)',
        description: { pt: 'Devolve o arco seno de um número', fr: 'Renvoie l\'arc sinus d\'un nombre' },
        category: 'math',
    },
    ASINH: {
        syntax: '(num)',
        description: { pt: 'Devolve o seno hiperbólico inverso', fr: 'Renvoie le sinus hyperbolique inverse' },
        category: 'math',
    },
    ATAN: {
        syntax: '(num)',
        description: { pt: 'Devolve o arco tangente de um número', fr: 'Renvoie l\'arc tangente d\'un nombre' },
        category: 'math',
    },
    ATAN2: {
        syntax: '(x; y)',
        description: { pt: 'Devolve o arco tangente das coordenadas x e y', fr: 'Renvoie l\'arc tangente des coordonnées x et y' },
        category: 'math',
    },
    ATANH: {
        syntax: '(num)',
        description: { pt: 'Devolve a tangente hiperbólica inversa', fr: 'Renvoie la tangente hyperbolique inverse' },
        category: 'math',
    },
    BASE: {
        syntax: '(num; base; [comprimento_min])',
        description: { pt: 'Converte um número para texto numa base dada', fr: 'Convertit un nombre en texte dans une base donnée' },
        category: 'math',
    },
    CEILING: {
        syntax: '(num; significância)',
        description: { pt: 'Arredonda para cima ao múltiplo mais próximo', fr: 'Arrondit au multiple supérieur le plus proche' },
        category: 'math',
    },
    'CEILING.MATH': {
        syntax: '(num; [significância]; [modo])',
        description: { pt: 'Arredonda para cima ao múltiplo mais próximo', fr: 'Arrondit au multiple supérieur le plus proche' },
        category: 'math',
    },
    COMBIN: {
        syntax: '(num; num_escolhido)',
        description: { pt: 'Devolve o número de combinações', fr: 'Renvoie le nombre de combinaisons' },
        category: 'math',
    },
    COMBINA: {
        syntax: '(num; num_escolhido)',
        description: { pt: 'Devolve combinações com repetições', fr: 'Renvoie les combinaisons avec répétitions' },
        category: 'math',
    },
    COS: {
        syntax: '(num)',
        description: { pt: 'Devolve o cosseno de um ângulo', fr: 'Renvoie le cosinus d\'un angle' },
        category: 'math',
    },
    COSH: {
        syntax: '(num)',
        description: { pt: 'Devolve o cosseno hiperbólico', fr: 'Renvoie le cosinus hyperbolique' },
        category: 'math',
    },
    COT: {
        syntax: '(num)',
        description: { pt: 'Devolve a cotangente de um ângulo', fr: 'Renvoie la cotangente d\'un angle' },
        category: 'math',
    },
    COTH: {
        syntax: '(num)',
        description: { pt: 'Devolve a cotangente hiperbólica', fr: 'Renvoie la cotangente hyperbolique' },
        category: 'math',
    },
    CSC: {
        syntax: '(num)',
        description: { pt: 'Devolve a cossecante de um ângulo', fr: 'Renvoie la cosécante d\'un angle' },
        category: 'math',
    },
    CSCH: {
        syntax: '(num)',
        description: { pt: 'Devolve a cossecante hiperbólica', fr: 'Renvoie la cosécante hyperbolique' },
        category: 'math',
    },
    DECIMAL: {
        syntax: '(texto; base)',
        description: { pt: 'Converte texto numa base para número decimal', fr: 'Convertit un texte d\'une base en nombre décimal' },
        category: 'math',
    },
    DEGREES: {
        syntax: '(num)',
        description: { pt: 'Converte radianos em graus', fr: 'Convertit des radians en degrés' },
        category: 'math',
    },
    EVEN: {
        syntax: '(num)',
        description: { pt: 'Arredonda para o inteiro par mais próximo', fr: 'Arrondit au nombre entier pair le plus proche' },
        category: 'math',
    },
    EXP: {
        syntax: '(num)',
        description: { pt: 'Devolve e elevado a uma potência', fr: 'Renvoie e élevé à une puissance' },
        category: 'math',
    },
    FACT: {
        syntax: '(num)',
        description: { pt: 'Devolve o fatorial de um número', fr: 'Renvoie la factorielle d\'un nombre' },
        category: 'math',
    },
    FACTDOUBLE: {
        syntax: '(num)',
        description: { pt: 'Devolve o fatorial duplo de um número', fr: 'Renvoie la factorielle double d\'un nombre' },
        category: 'math',
    },
    FLOOR: {
        syntax: '(num; significância)',
        description: { pt: 'Arredonda para baixo ao múltiplo mais próximo', fr: 'Arrondit au multiple inférieur le plus proche' },
        category: 'math',
    },
    'FLOOR.MATH': {
        syntax: '(num; [significância]; [modo])',
        description: { pt: 'Arredonda para baixo ao múltiplo mais próximo', fr: 'Arrondit au multiple inférieur le plus proche' },
        category: 'math',
    },
    GCD: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Devolve o máximo divisor comum', fr: 'Renvoie le plus grand commun diviseur' },
        category: 'math',
    },
    INT: {
        syntax: '(num)',
        description: { pt: 'Arredonda para o inteiro inferior mais próximo', fr: 'Arrondit au nombre entier inférieur' },
        category: 'math',
    },
    LCM: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Devolve o mínimo múltiplo comum', fr: 'Renvoie le plus petit commun multiple' },
        category: 'math',
    },
    LN: {
        syntax: '(num)',
        description: { pt: 'Devolve o logaritmo natural de um número', fr: 'Renvoie le logarithme naturel d\'un nombre' },
        category: 'math',
    },
    LOG: {
        syntax: '(num; [base])',
        description: { pt: 'Devolve o logaritmo de um número numa base', fr: 'Renvoie le logarithme d\'un nombre dans une base' },
        category: 'math',
    },
    LOG10: {
        syntax: '(num)',
        description: { pt: 'Devolve o logaritmo de base 10', fr: 'Renvoie le logarithme en base 10' },
        category: 'math',
    },
    MOD: {
        syntax: '(num; divisor)',
        description: { pt: 'Devolve o resto da divisão', fr: 'Renvoie le reste de la division' },
        category: 'math',
    },
    MROUND: {
        syntax: '(num; múltiplo)',
        description: { pt: 'Arredonda ao múltiplo desejado', fr: 'Arrondit au multiple souhaité' },
        category: 'math',
    },
    MULTINOMIAL: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Devolve o multinomial de um conjunto', fr: 'Renvoie le multinôme d\'un ensemble' },
        category: 'math',
    },
    ODD: {
        syntax: '(num)',
        description: { pt: 'Arredonda para o inteiro ímpar mais próximo', fr: 'Arrondit au nombre entier impair le plus proche' },
        category: 'math',
    },
    PI: {
        syntax: '()',
        description: { pt: 'Devolve o valor de PI', fr: 'Renvoie la valeur de PI' },
        category: 'math',
    },
    POWER: {
        syntax: '(num; potência)',
        description: { pt: 'Devolve o resultado de um número elevado', fr: 'Renvoie un nombre élevé à une puissance' },
        category: 'math',
    },
    PRODUCT: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Multiplica todos os números dados', fr: 'Multiplie tous les nombres donnés' },
        category: 'math',
    },
    QUOTIENT: {
        syntax: '(numerador; denominador)',
        description: { pt: 'Devolve a parte inteira da divisão', fr: 'Renvoie la partie entière de la division' },
        category: 'math',
    },
    RADIANS: {
        syntax: '(num)',
        description: { pt: 'Converte graus em radianos', fr: 'Convertit des degrés en radians' },
        category: 'math',
    },
    RAND: {
        syntax: '()',
        description: { pt: 'Devolve um número aleatório entre 0 e 1', fr: 'Renvoie un nombre aléatoire entre 0 et 1' },
        category: 'math',
    },
    RANDBETWEEN: {
        syntax: '(min; max)',
        description: { pt: 'Devolve um inteiro aleatório entre dois valores', fr: 'Renvoie un entier aléatoire entre deux valeurs' },
        category: 'math',
    },
    ROMAN: {
        syntax: '(num; [forma])',
        description: { pt: 'Converte número arábico em numeral romano', fr: 'Convertit un nombre arabe en chiffre romain' },
        category: 'math',
    },
    ROUND: {
        syntax: '(num; decimais)',
        description: { pt: 'Arredonda um número ao número de dígitos', fr: 'Arrondit un nombre au nombre de décimales' },
        category: 'math',
    },
    ROUNDDOWN: {
        syntax: '(num; decimais)',
        description: { pt: 'Arredonda um número para baixo', fr: 'Arrondit un nombre vers le bas' },
        category: 'math',
    },
    ROUNDUP: {
        syntax: '(num; decimais)',
        description: { pt: 'Arredonda um número para cima', fr: 'Arrondit un nombre vers le haut' },
        category: 'math',
    },
    SEC: {
        syntax: '(num)',
        description: { pt: 'Devolve a secante de um ângulo', fr: 'Renvoie la sécante d\'un angle' },
        category: 'math',
    },
    SECH: {
        syntax: '(num)',
        description: { pt: 'Devolve a secante hiperbólica', fr: 'Renvoie la sécante hyperbolique' },
        category: 'math',
    },
    SERIESSUM: {
        syntax: '(x; n; m; coefs)',
        description: { pt: 'Devolve a soma de uma série de potências', fr: 'Renvoie la somme d\'une série de puissances' },
        category: 'math',
    },
    SIGN: {
        syntax: '(num)',
        description: { pt: 'Devolve o sinal de um número', fr: 'Renvoie le signe d\'un nombre' },
        category: 'math',
    },
    SIN: {
        syntax: '(num)',
        description: { pt: 'Devolve o seno de um ângulo', fr: 'Renvoie le sinus d\'un angle' },
        category: 'math',
    },
    SINH: {
        syntax: '(num)',
        description: { pt: 'Devolve o seno hiperbólico', fr: 'Renvoie le sinus hyperbolique' },
        category: 'math',
    },
    SQRT: {
        syntax: '(num)',
        description: { pt: 'Devolve a raiz quadrada de um número', fr: 'Renvoie la racine carrée d\'un nombre' },
        category: 'math',
    },
    SQRTPI: {
        syntax: '(num)',
        description: { pt: 'Devolve a raiz quadrada de num * PI', fr: 'Renvoie la racine carrée de num * PI' },
        category: 'math',
    },
    SUBTOTAL: {
        syntax: '(num_função; ref1; ...)',
        description: { pt: 'Devolve um subtotal numa lista ou base', fr: 'Renvoie un sous-total dans une liste ou base' },
        category: 'math',
    },
    SUM: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Soma todos os números dados', fr: 'Additionne tous les nombres donnés' },
        category: 'math',
    },
    SUMIF: {
        syntax: '(intervalo; critério; [intervalo_soma])',
        description: { pt: 'Soma células que satisfazem um critério', fr: 'Additionne les cellules qui satisfont un critère' },
        category: 'math',
    },
    SUMIFS: {
        syntax: '(intervalo_soma; intervalo1; critério1; ...)',
        description: { pt: 'Soma células que satisfazem vários critérios', fr: 'Additionne les cellules selon plusieurs critères' },
        category: 'math',
    },
    SUMPRODUCT: {
        syntax: '(matriz1; [matriz2]; ...)',
        description: { pt: 'Devolve a soma dos produtos de matrizes', fr: 'Renvoie la somme des produits de matrices' },
        category: 'math',
    },
    SUMSQ: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Devolve a soma dos quadrados', fr: 'Renvoie la somme des carrés' },
        category: 'math',
    },
    SUMX2MY2: {
        syntax: '(matriz_x; matriz_y)',
        description: { pt: 'Soma das diferenças dos quadrados', fr: 'Somme des différences des carrés' },
        category: 'math',
    },
    SUMX2PY2: {
        syntax: '(matriz_x; matriz_y)',
        description: { pt: 'Soma das somas dos quadrados', fr: 'Somme des sommes des carrés' },
        category: 'math',
    },
    SUMXMY2: {
        syntax: '(matriz_x; matriz_y)',
        description: { pt: 'Soma dos quadrados das diferenças', fr: 'Somme des carrés des différences' },
        category: 'math',
    },
    TAN: {
        syntax: '(num)',
        description: { pt: 'Devolve a tangente de um ângulo', fr: 'Renvoie la tangente d\'un angle' },
        category: 'math',
    },
    TANH: {
        syntax: '(num)',
        description: { pt: 'Devolve a tangente hiperbólica', fr: 'Renvoie la tangente hyperbolique' },
        category: 'math',
    },
    TRUNC: {
        syntax: '(num; [decimais])',
        description: { pt: 'Trunca um número para um inteiro', fr: 'Tronque un nombre en un entier' },
        category: 'math',
    },
    N: {
        syntax: '(valor)',
        description: { pt: 'Converte um valor para número', fr: 'Convertit une valeur en nombre' },
        category: 'math',
    },

    // ==================== TEXT ====================
    CHAR: {
        syntax: '(num)',
        description: { pt: 'Devolve o carácter do código ASCII', fr: 'Renvoie le caractère du code ASCII' },
        category: 'text',
    },
    CLEAN: {
        syntax: '(texto)',
        description: { pt: 'Remove caracteres não imprimíveis do texto', fr: 'Supprime les caractères non imprimables du texte' },
        category: 'text',
    },
    CODE: {
        syntax: '(texto)',
        description: { pt: 'Devolve o código ASCII do primeiro carácter', fr: 'Renvoie le code ASCII du premier caractère' },
        category: 'text',
    },
    CONCATENATE: {
        syntax: '(texto1; [texto2]; ...)',
        description: { pt: 'Junta vários textos num só', fr: 'Joint plusieurs textes en un seul' },
        category: 'text',
    },
    EXACT: {
        syntax: '(texto1; texto2)',
        description: { pt: 'Verifica se dois textos são idênticos', fr: 'Vérifie si deux textes sont identiques' },
        category: 'text',
    },
    FIND: {
        syntax: '(texto_procurado; texto; [pos_inicial])',
        description: { pt: 'Localiza texto dentro de outro (sensível)', fr: 'Recherche un texte dans un autre (sensible)' },
        category: 'text',
    },
    LEFT: {
        syntax: '(texto; [num_caracteres])',
        description: { pt: 'Devolve os primeiros caracteres de um texto', fr: 'Renvoie les premiers caractères d\'un texte' },
        category: 'text',
    },
    LEN: {
        syntax: '(texto)',
        description: { pt: 'Devolve o número de caracteres do texto', fr: 'Renvoie le nombre de caractères du texte' },
        category: 'text',
    },
    LOWER: {
        syntax: '(texto)',
        description: { pt: 'Converte texto em minúsculas', fr: 'Convertit le texte en minuscules' },
        category: 'text',
    },
    MID: {
        syntax: '(texto; pos_inicial; num_caracteres)',
        description: { pt: 'Devolve caracteres do meio de um texto', fr: 'Renvoie des caractères au milieu d\'un texte' },
        category: 'text',
    },
    PROPER: {
        syntax: '(texto)',
        description: { pt: 'Coloca a primeira letra em maiúscula', fr: 'Met la première lettre en majuscule' },
        category: 'text',
    },
    REPLACE: {
        syntax: '(texto_antigo; pos_inicial; num_caracteres; novo_texto)',
        description: { pt: 'Substitui parte de um texto por outro', fr: 'Remplace une partie d\'un texte par un autre' },
        category: 'text',
    },
    REPT: {
        syntax: '(texto; num_vezes)',
        description: { pt: 'Repete um texto um número de vezes', fr: 'Répète un texte un nombre de fois' },
        category: 'text',
    },
    RIGHT: {
        syntax: '(texto; [num_caracteres])',
        description: { pt: 'Devolve os últimos caracteres de um texto', fr: 'Renvoie les derniers caractères d\'un texte' },
        category: 'text',
    },
    SEARCH: {
        syntax: '(texto_procurado; texto; [pos_inicial])',
        description: { pt: 'Localiza texto dentro de outro (insensível)', fr: 'Recherche un texte dans un autre (insensible)' },
        category: 'text',
    },
    SUBSTITUTE: {
        syntax: '(texto; texto_antigo; novo_texto; [num_ocorrência])',
        description: { pt: 'Substitui texto antigo por novo texto', fr: 'Remplace l\'ancien texte par un nouveau texte' },
        category: 'text',
    },
    T: {
        syntax: '(valor)',
        description: { pt: 'Devolve o texto se o valor for texto', fr: 'Renvoie le texte si la valeur est du texte' },
        category: 'text',
    },
    TEXT: {
        syntax: '(valor; formato)',
        description: { pt: 'Formata um número como texto', fr: 'Formate un nombre en texte' },
        category: 'text',
    },
    TRIM: {
        syntax: '(texto)',
        description: { pt: 'Remove espaços extra de um texto', fr: 'Supprime les espaces superflus d\'un texte' },
        category: 'text',
    },
    UNICHAR: {
        syntax: '(num)',
        description: { pt: 'Devolve o carácter Unicode', fr: 'Renvoie le caractère Unicode' },
        category: 'text',
    },
    UNICODE: {
        syntax: '(texto)',
        description: { pt: 'Devolve o código Unicode do primeiro carácter', fr: 'Renvoie le code Unicode du premier caractère' },
        category: 'text',
    },
    UPPER: {
        syntax: '(texto)',
        description: { pt: 'Converte texto em maiúsculas', fr: 'Convertit le texte en majuscules' },
        category: 'text',
    },
    FORMULATEXT: {
        syntax: '(ref)',
        description: { pt: 'Devolve a fórmula como texto', fr: 'Renvoie la formule sous forme de texte' },
        category: 'text',
    },
    HYPERLINK: {
        syntax: '(url; [nome_amigável])',
        description: { pt: 'Cria uma hiperligação', fr: 'Crée un lien hypertexte' },
        category: 'text',
    },

    // ==================== DATE ====================
    DATE: {
        syntax: '(ano; mês; dia)',
        description: { pt: 'Cria uma data a partir de ano, mês e dia', fr: 'Crée une date à partir d\'année, mois et jour' },
        category: 'date',
    },
    DATEDIF: {
        syntax: '(data_inicial; data_final; unidade)',
        description: { pt: 'Calcula a diferença entre duas datas', fr: 'Calcule la différence entre deux dates' },
        category: 'date',
    },
    DATEVALUE: {
        syntax: '(texto_data)',
        description: { pt: 'Converte texto de data em número de série', fr: 'Convertit un texte de date en numéro de série' },
        category: 'date',
    },
    DAY: {
        syntax: '(data)',
        description: { pt: 'Devolve o dia de uma data', fr: 'Renvoie le jour d\'une date' },
        category: 'date',
    },
    DAYS: {
        syntax: '(data_final; data_inicial)',
        description: { pt: 'Devolve o número de dias entre duas datas', fr: 'Renvoie le nombre de jours entre deux dates' },
        category: 'date',
    },
    DAYS360: {
        syntax: '(data_inicial; data_final; [método])',
        description: { pt: 'Calcula dias entre datas (ano de 360 dias)', fr: 'Calcule les jours entre dates (année de 360j)' },
        category: 'date',
    },
    EDATE: {
        syntax: '(data_inicial; meses)',
        description: { pt: 'Devolve uma data com meses adicionados', fr: 'Renvoie une date avec des mois ajoutés' },
        category: 'date',
    },
    EOMONTH: {
        syntax: '(data_inicial; meses)',
        description: { pt: 'Devolve o último dia do mês futuro/passado', fr: 'Renvoie le dernier jour du mois futur/passé' },
        category: 'date',
    },
    HOUR: {
        syntax: '(num_série)',
        description: { pt: 'Devolve a hora de um valor de tempo', fr: 'Renvoie l\'heure d\'une valeur de temps' },
        category: 'date',
    },
    ISOWEEKNUM: {
        syntax: '(data)',
        description: { pt: 'Devolve o número da semana ISO do ano', fr: 'Renvoie le numéro de semaine ISO de l\'année' },
        category: 'date',
    },
    MINUTE: {
        syntax: '(num_série)',
        description: { pt: 'Devolve os minutos de um valor de tempo', fr: 'Renvoie les minutes d\'une valeur de temps' },
        category: 'date',
    },
    MONTH: {
        syntax: '(data)',
        description: { pt: 'Devolve o mês de uma data', fr: 'Renvoie le mois d\'une date' },
        category: 'date',
    },
    NETWORKDAYS: {
        syntax: '(data_inicial; data_final; [feriados])',
        description: { pt: 'Devolve o número de dias úteis entre datas', fr: 'Renvoie le nombre de jours ouvrés entre dates' },
        category: 'date',
    },
    'NETWORKDAYS.INTL': {
        syntax: '(data_inicial; data_final; [fim_semana]; [feriados])',
        description: { pt: 'Dias úteis entre datas com fim de semana personalizado', fr: 'Jours ouvrés entre dates avec week-end personnalisé' },
        category: 'date',
    },
    NOW: {
        syntax: '()',
        description: { pt: 'Devolve a data e hora atuais', fr: 'Renvoie la date et l\'heure actuelles' },
        category: 'date',
    },
    SECOND: {
        syntax: '(num_série)',
        description: { pt: 'Devolve os segundos de um valor de tempo', fr: 'Renvoie les secondes d\'une valeur de temps' },
        category: 'date',
    },
    TIME: {
        syntax: '(hora; minuto; segundo)',
        description: { pt: 'Cria um valor de tempo', fr: 'Crée une valeur de temps' },
        category: 'date',
    },
    TIMEVALUE: {
        syntax: '(texto_hora)',
        description: { pt: 'Converte texto de hora em número de série', fr: 'Convertit un texte d\'heure en numéro de série' },
        category: 'date',
    },
    TODAY: {
        syntax: '()',
        description: { pt: 'Devolve a data de hoje', fr: 'Renvoie la date d\'aujourd\'hui' },
        category: 'date',
    },
    WEEKDAY: {
        syntax: '(data; [tipo_retorno])',
        description: { pt: 'Devolve o dia da semana de uma data', fr: 'Renvoie le jour de la semaine d\'une date' },
        category: 'date',
    },
    WEEKNUM: {
        syntax: '(data; [tipo_retorno])',
        description: { pt: 'Devolve o número da semana do ano', fr: 'Renvoie le numéro de la semaine de l\'année' },
        category: 'date',
    },
    WORKDAY: {
        syntax: '(data_inicial; dias; [feriados])',
        description: { pt: 'Devolve a data após N dias úteis', fr: 'Renvoie la date après N jours ouvrés' },
        category: 'date',
    },
    'WORKDAY.INTL': {
        syntax: '(data_inicial; dias; [fim_semana]; [feriados])',
        description: { pt: 'Data após N dias úteis com fim de semana personalizado', fr: 'Date après N jours ouvrés avec week-end personnalisé' },
        category: 'date',
    },
    YEAR: {
        syntax: '(data)',
        description: { pt: 'Devolve o ano de uma data', fr: 'Renvoie l\'année d\'une date' },
        category: 'date',
    },
    YEARFRAC: {
        syntax: '(data_inicial; data_final; [base])',
        description: { pt: 'Devolve a fração do ano entre duas datas', fr: 'Renvoie la fraction d\'année entre deux dates' },
        category: 'date',
    },

    // ==================== LOGICAL ====================
    AND: {
        syntax: '(lógico1; [lógico2]; ...)',
        description: { pt: 'Devolve VERDADEIRO se todos forem verdadeiros', fr: 'Renvoie VRAI si tous les arguments sont vrais' },
        category: 'logical',
    },
    FALSE: {
        syntax: '()',
        description: { pt: 'Devolve o valor lógico FALSO', fr: 'Renvoie la valeur logique FAUX' },
        category: 'logical',
    },
    IF: {
        syntax: '(condição; valor_se_verdadeiro; [valor_se_falso])',
        description: { pt: 'Devolve um valor consoante a condição', fr: 'Renvoie une valeur selon la condition' },
        category: 'logical',
    },
    IFERROR: {
        syntax: '(valor; valor_se_erro)',
        description: { pt: 'Devolve valor alternativo se houver erro', fr: 'Renvoie une valeur alternative en cas d\'erreur' },
        category: 'logical',
    },
    IFNA: {
        syntax: '(valor; valor_se_na)',
        description: { pt: 'Devolve valor alternativo se for #N/D', fr: 'Renvoie une valeur alternative si #N/A' },
        category: 'logical',
    },
    IFS: {
        syntax: '(condição1; valor1; [condição2; valor2]; ...)',
        description: { pt: 'Avalia múltiplas condições sequencialmente', fr: 'Évalue plusieurs conditions séquentiellement' },
        category: 'logical',
    },
    NOT: {
        syntax: '(lógico)',
        description: { pt: 'Inverte o valor lógico', fr: 'Inverse la valeur logique' },
        category: 'logical',
    },
    OR: {
        syntax: '(lógico1; [lógico2]; ...)',
        description: { pt: 'Devolve VERDADEIRO se algum for verdadeiro', fr: 'Renvoie VRAI si un argument est vrai' },
        category: 'logical',
    },
    SWITCH: {
        syntax: '(expressão; valor1; resultado1; [valor2; resultado2]; ...; [padrão])',
        description: { pt: 'Avalia expressão contra uma lista de valores', fr: 'Évalue l\'expression par rapport à une liste' },
        category: 'logical',
    },
    TRUE: {
        syntax: '()',
        description: { pt: 'Devolve o valor lógico VERDADEIRO', fr: 'Renvoie la valeur logique VRAI' },
        category: 'logical',
    },
    XOR: {
        syntax: '(lógico1; [lógico2]; ...)',
        description: { pt: 'Devolve OU exclusivo lógico', fr: 'Renvoie un OU exclusif logique' },
        category: 'logical',
    },

    // ==================== STATISTICAL ====================
    AVEDEV: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Devolve a média dos desvios absolutos', fr: 'Renvoie la moyenne des écarts absolus' },
        category: 'statistical',
    },
    AVERAGE: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Devolve a média aritmética dos argumentos', fr: 'Renvoie la moyenne arithmétique des arguments' },
        category: 'statistical',
    },
    AVERAGEA: {
        syntax: '(valor1; [valor2]; ...)',
        description: { pt: 'Devolve a média incluindo texto e lógicos', fr: 'Renvoie la moyenne incluant texte et logiques' },
        category: 'statistical',
    },
    AVERAGEIF: {
        syntax: '(intervalo; critério; [intervalo_média])',
        description: { pt: 'Média das células que satisfazem um critério', fr: 'Moyenne des cellules qui satisfont un critère' },
        category: 'statistical',
    },
    'BETA.DIST': {
        syntax: '(x; alfa; beta; cumulativo; [A]; [B])',
        description: { pt: 'Devolve a distribuição beta', fr: 'Renvoie la distribution bêta' },
        category: 'statistical',
    },
    'BETA.INV': {
        syntax: '(probabilidade; alfa; beta; [A]; [B])',
        description: { pt: 'Devolve o inverso da distribuição beta', fr: 'Renvoie l\'inverse de la distribution bêta' },
        category: 'statistical',
    },
    BETADIST: {
        syntax: '(x; alfa; beta; [A]; [B])',
        description: { pt: 'Versão de compatibilidade de DIST.BETA', fr: 'Version de compatibilité de LOI.BETA' },
        category: 'statistical',
    },
    BETAINV: {
        syntax: '(probabilidade; alfa; beta; [A]; [B])',
        description: { pt: 'Versão de compatibilidade de INV.BETA', fr: 'Version de compatibilité de BETA.INVERSE' },
        category: 'statistical',
    },
    'BINOM.DIST': {
        syntax: '(núm_êxitos; tentativas; prob_êxito; cumulativo)',
        description: { pt: 'Devolve a distribuição binomial', fr: 'Renvoie la distribution binomiale' },
        category: 'statistical',
    },
    'BINOM.INV': {
        syntax: '(tentativas; prob_êxito; alfa)',
        description: { pt: 'Devolve o inverso da distribuição binomial', fr: 'Renvoie l\'inverse de la distribution binomiale' },
        category: 'statistical',
    },
    BINOMDIST: {
        syntax: '(núm_êxitos; tentativas; prob_êxito; cumulativo)',
        description: { pt: 'Versão de compatibilidade de DIST.BINOM', fr: 'Version de compatibilité de LOI.BINOMIALE' },
        category: 'statistical',
    },
    CHIDIST: {
        syntax: '(x; graus_liberdade)',
        description: { pt: 'Versão de compatibilidade de DIST.QUI', fr: 'Version de compatibilité de LOI.KHIDEUX' },
        category: 'statistical',
    },
    CHIINV: {
        syntax: '(probabilidade; graus_liberdade)',
        description: { pt: 'Versão de compatibilidade de INV.QUI', fr: 'Version de compatibilité de KHIDEUX.INVERSE' },
        category: 'statistical',
    },
    'CHISQ.DIST': {
        syntax: '(x; graus_liberdade; cumulativo)',
        description: { pt: 'Devolve a distribuição qui-quadrado', fr: 'Renvoie la distribution du khi-deux' },
        category: 'statistical',
    },
    'CHISQ.DIST.RT': {
        syntax: '(x; graus_liberdade)',
        description: { pt: 'Distribuição qui-quadrado à direita', fr: 'Distribution du khi-deux à droite' },
        category: 'statistical',
    },
    'CHISQ.INV': {
        syntax: '(probabilidade; graus_liberdade)',
        description: { pt: 'Inverso da distribuição qui-quadrado', fr: 'Inverse de la distribution du khi-deux' },
        category: 'statistical',
    },
    'CHISQ.INV.RT': {
        syntax: '(probabilidade; graus_liberdade)',
        description: { pt: 'Inverso da distribuição qui-quadrado à direita', fr: 'Inverse de la distribution khi-deux à droite' },
        category: 'statistical',
    },
    'CHISQ.TEST': {
        syntax: '(intervalo_real; intervalo_esperado)',
        description: { pt: 'Devolve o teste qui-quadrado', fr: 'Renvoie le test du khi-deux' },
        category: 'statistical',
    },
    CHITEST: {
        syntax: '(intervalo_real; intervalo_esperado)',
        description: { pt: 'Versão de compatibilidade de TESTE.QUI', fr: 'Version de compatibilité de TEST.KHIDEUX' },
        category: 'statistical',
    },
    CONFIDENCE: {
        syntax: '(alfa; desvio_padrão; tamanho)',
        description: { pt: 'Versão de compatibilidade de INT.CONFIANÇA', fr: 'Version de compatibilité de INTERVALLE.CONFIANCE' },
        category: 'statistical',
    },
    'CONFIDENCE.NORM': {
        syntax: '(alfa; desvio_padrão; tamanho)',
        description: { pt: 'Intervalo de confiança (distribuição normal)', fr: 'Intervalle de confiance (distribution normale)' },
        category: 'statistical',
    },
    'CONFIDENCE.T': {
        syntax: '(alfa; desvio_padrão; tamanho)',
        description: { pt: 'Intervalo de confiança (distribuição T)', fr: 'Intervalle de confiance (distribution T)' },
        category: 'statistical',
    },
    CORREL: {
        syntax: '(matriz1; matriz2)',
        description: { pt: 'Devolve o coeficiente de correlação', fr: 'Renvoie le coefficient de corrélation' },
        category: 'statistical',
    },
    COUNT: {
        syntax: '(valor1; [valor2]; ...)',
        description: { pt: 'Conta as células com números', fr: 'Compte les cellules contenant des nombres' },
        category: 'statistical',
    },
    COUNTA: {
        syntax: '(valor1; [valor2]; ...)',
        description: { pt: 'Conta as células não vazias', fr: 'Compte les cellules non vides' },
        category: 'statistical',
    },
    COUNTBLANK: {
        syntax: '(intervalo)',
        description: { pt: 'Conta as células vazias', fr: 'Compte les cellules vides' },
        category: 'statistical',
    },
    COUNTIF: {
        syntax: '(intervalo; critério)',
        description: { pt: 'Conta células que satisfazem um critério', fr: 'Compte les cellules qui satisfont un critère' },
        category: 'statistical',
    },
    COUNTIFS: {
        syntax: '(intervalo1; critério1; [intervalo2; critério2]; ...)',
        description: { pt: 'Conta células que satisfazem vários critérios', fr: 'Compte les cellules selon plusieurs critères' },
        category: 'statistical',
    },
    COVAR: {
        syntax: '(matriz1; matriz2)',
        description: { pt: 'Versão de compatibilidade de COVARIÂNCIA', fr: 'Version de compatibilité de COVARIANCE' },
        category: 'statistical',
    },
    'COVARIANCE.P': {
        syntax: '(matriz1; matriz2)',
        description: { pt: 'Devolve a covariância da população', fr: 'Renvoie la covariance de la population' },
        category: 'statistical',
    },
    'COVARIANCE.S': {
        syntax: '(matriz1; matriz2)',
        description: { pt: 'Devolve a covariância da amostra', fr: 'Renvoie la covariance de l\'échantillon' },
        category: 'statistical',
    },
    CRITBINOM: {
        syntax: '(tentativas; prob_êxito; alfa)',
        description: { pt: 'Versão de compatibilidade de INV.BINOM', fr: 'Version de compatibilité de LOI.BINOMIALE.INVERSE' },
        category: 'statistical',
    },
    DEVSQ: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Soma dos quadrados dos desvios', fr: 'Somme des carrés des écarts' },
        category: 'statistical',
    },
    'EXPON.DIST': {
        syntax: '(x; lambda; cumulativo)',
        description: { pt: 'Devolve a distribuição exponencial', fr: 'Renvoie la distribution exponentielle' },
        category: 'statistical',
    },
    EXPONDIST: {
        syntax: '(x; lambda; cumulativo)',
        description: { pt: 'Versão de compatibilidade de DIST.EXPON', fr: 'Version de compatibilité de LOI.EXPONENTIELLE' },
        category: 'statistical',
    },
    'F.DIST': {
        syntax: '(x; graus_lib1; graus_lib2; cumulativo)',
        description: { pt: 'Devolve a distribuição F', fr: 'Renvoie la distribution F' },
        category: 'statistical',
    },
    'F.DIST.RT': {
        syntax: '(x; graus_lib1; graus_lib2)',
        description: { pt: 'Distribuição F à direita', fr: 'Distribution F à droite' },
        category: 'statistical',
    },
    'F.INV': {
        syntax: '(probabilidade; graus_lib1; graus_lib2)',
        description: { pt: 'Inverso da distribuição F', fr: 'Inverse de la distribution F' },
        category: 'statistical',
    },
    'F.INV.RT': {
        syntax: '(probabilidade; graus_lib1; graus_lib2)',
        description: { pt: 'Inverso da distribuição F à direita', fr: 'Inverse de la distribution F à droite' },
        category: 'statistical',
    },
    'F.TEST': {
        syntax: '(matriz1; matriz2)',
        description: { pt: 'Devolve o resultado do teste F', fr: 'Renvoie le résultat du test F' },
        category: 'statistical',
    },
    FDIST: {
        syntax: '(x; graus_lib1; graus_lib2)',
        description: { pt: 'Versão de compatibilidade de DIST.F', fr: 'Version de compatibilité de LOI.F' },
        category: 'statistical',
    },
    FINV: {
        syntax: '(probabilidade; graus_lib1; graus_lib2)',
        description: { pt: 'Versão de compatibilidade de INV.F', fr: 'Version de compatibilité de INVERSE.LOI.F' },
        category: 'statistical',
    },
    FISHER: {
        syntax: '(x)',
        description: { pt: 'Devolve a transformação Fisher', fr: 'Renvoie la transformation de Fisher' },
        category: 'statistical',
    },
    FISHERINV: {
        syntax: '(y)',
        description: { pt: 'Devolve o inverso da transformação Fisher', fr: 'Renvoie l\'inverse de la transformation de Fisher' },
        category: 'statistical',
    },
    FTEST: {
        syntax: '(matriz1; matriz2)',
        description: { pt: 'Versão de compatibilidade de TESTE.F', fr: 'Version de compatibilité de TEST.F' },
        category: 'statistical',
    },
    GAMMA: {
        syntax: '(num)',
        description: { pt: 'Devolve o valor da função gama', fr: 'Renvoie la valeur de la fonction gamma' },
        category: 'statistical',
    },
    'GAMMA.DIST': {
        syntax: '(x; alfa; beta; cumulativo)',
        description: { pt: 'Devolve a distribuição gama', fr: 'Renvoie la distribution gamma' },
        category: 'statistical',
    },
    'GAMMA.INV': {
        syntax: '(probabilidade; alfa; beta)',
        description: { pt: 'Inverso da distribuição gama', fr: 'Inverse de la distribution gamma' },
        category: 'statistical',
    },
    GAMMADIST: {
        syntax: '(x; alfa; beta; cumulativo)',
        description: { pt: 'Versão de compatibilidade de DIST.GAMA', fr: 'Version de compatibilité de LOI.GAMMA' },
        category: 'statistical',
    },
    GAMMAINV: {
        syntax: '(probabilidade; alfa; beta)',
        description: { pt: 'Versão de compatibilidade de INV.GAMA', fr: 'Version de compatibilité de GAMMA.INVERSE' },
        category: 'statistical',
    },
    GAMMALN: {
        syntax: '(x)',
        description: { pt: 'Logaritmo natural da função gama', fr: 'Logarithme naturel de la fonction gamma' },
        category: 'statistical',
    },
    'GAMMALN.PRECISE': {
        syntax: '(x)',
        description: { pt: 'Logaritmo natural da função gama (preciso)', fr: 'Logarithme naturel de la fonction gamma (précis)' },
        category: 'statistical',
    },
    GAUSS: {
        syntax: '(z)',
        description: { pt: 'Devolve a probabilidade de Gauss', fr: 'Renvoie la probabilité de Gauss' },
        category: 'statistical',
    },
    GEOMEAN: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Devolve a média geométrica', fr: 'Renvoie la moyenne géométrique' },
        category: 'statistical',
    },
    HARMEAN: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Devolve a média harmónica', fr: 'Renvoie la moyenne harmonique' },
        category: 'statistical',
    },
    'HYPGEOM.DIST': {
        syntax: '(êxitos_amostra; tamanho_amostra; êxitos_pop; tamanho_pop; cumulativo)',
        description: { pt: 'Devolve a distribuição hipergeométrica', fr: 'Renvoie la distribution hypergéométrique' },
        category: 'statistical',
    },
    HYPGEOMDIST: {
        syntax: '(êxitos_amostra; tamanho_amostra; êxitos_pop; tamanho_pop)',
        description: { pt: 'Versão de compatibilidade de DIST.HIPERGEOM', fr: 'Version de compatibilité de LOI.HYPERGEOMETRIQUE' },
        category: 'statistical',
    },
    LARGE: {
        syntax: '(matriz; k)',
        description: { pt: 'Devolve o k-ésimo maior valor', fr: 'Renvoie la k-ième plus grande valeur' },
        category: 'statistical',
    },
    LOGINV: {
        syntax: '(probabilidade; média; desvio_padrão)',
        description: { pt: 'Versão de compatibilidade de INV.LOGNORMAL', fr: 'Version de compatibilité de LOI.LOGNORMALE.INVERSE' },
        category: 'statistical',
    },
    'LOGNORM.DIST': {
        syntax: '(x; média; desvio_padrão; cumulativo)',
        description: { pt: 'Devolve a distribuição lognormal', fr: 'Renvoie la distribution log-normale' },
        category: 'statistical',
    },
    'LOGNORM.INV': {
        syntax: '(probabilidade; média; desvio_padrão)',
        description: { pt: 'Inverso da distribuição lognormal', fr: 'Inverse de la distribution log-normale' },
        category: 'statistical',
    },
    LOGNORMDIST: {
        syntax: '(x; média; desvio_padrão)',
        description: { pt: 'Versão de compatibilidade de DIST.LOGNORMAL', fr: 'Version de compatibilité de LOI.LOGNORMALE' },
        category: 'statistical',
    },
    MAX: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Devolve o valor máximo', fr: 'Renvoie la valeur maximale' },
        category: 'statistical',
    },
    MAXA: {
        syntax: '(valor1; [valor2]; ...)',
        description: { pt: 'Devolve o valor máximo incluindo texto', fr: 'Renvoie la valeur maximale incluant le texte' },
        category: 'statistical',
    },
    MAXIFS: {
        syntax: '(intervalo_max; intervalo1; critério1; ...)',
        description: { pt: 'Valor máximo com múltiplos critérios', fr: 'Valeur maximale avec plusieurs critères' },
        category: 'statistical',
    },
    MEDIAN: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Devolve a mediana dos números', fr: 'Renvoie la médiane des nombres' },
        category: 'statistical',
    },
    MIN: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Devolve o valor mínimo', fr: 'Renvoie la valeur minimale' },
        category: 'statistical',
    },
    MINA: {
        syntax: '(valor1; [valor2]; ...)',
        description: { pt: 'Devolve o valor mínimo incluindo texto', fr: 'Renvoie la valeur minimale incluant le texte' },
        category: 'statistical',
    },
    MINIFS: {
        syntax: '(intervalo_min; intervalo1; critério1; ...)',
        description: { pt: 'Valor mínimo com múltiplos critérios', fr: 'Valeur minimale avec plusieurs critères' },
        category: 'statistical',
    },
    'NEGBINOM.DIST': {
        syntax: '(num_falhas; num_êxitos; prob_êxito; cumulativo)',
        description: { pt: 'Devolve a distribuição binomial negativa', fr: 'Renvoie la distribution binomiale négative' },
        category: 'statistical',
    },
    NEGBINOMDIST: {
        syntax: '(num_falhas; num_êxitos; prob_êxito)',
        description: { pt: 'Versão de compatibilidade de DIST.BIN.NEG', fr: 'Version de compatibilité de LOI.BINOMIALE.NEG' },
        category: 'statistical',
    },
    'NORM.DIST': {
        syntax: '(x; média; desvio_padrão; cumulativo)',
        description: { pt: 'Devolve a distribuição normal', fr: 'Renvoie la distribution normale' },
        category: 'statistical',
    },
    'NORM.INV': {
        syntax: '(probabilidade; média; desvio_padrão)',
        description: { pt: 'Inverso da distribuição normal', fr: 'Inverse de la distribution normale' },
        category: 'statistical',
    },
    'NORM.S.DIST': {
        syntax: '(z; cumulativo)',
        description: { pt: 'Distribuição normal padrão', fr: 'Distribution normale standard' },
        category: 'statistical',
    },
    'NORM.S.INV': {
        syntax: '(probabilidade)',
        description: { pt: 'Inverso da distribuição normal padrão', fr: 'Inverse de la distribution normale standard' },
        category: 'statistical',
    },
    NORMDIST: {
        syntax: '(x; média; desvio_padrão; cumulativo)',
        description: { pt: 'Versão de compatibilidade de DIST.NORM', fr: 'Version de compatibilité de LOI.NORMALE' },
        category: 'statistical',
    },
    NORMINV: {
        syntax: '(probabilidade; média; desvio_padrão)',
        description: { pt: 'Versão de compatibilidade de INV.NORM', fr: 'Version de compatibilité de LOI.NORMALE.INVERSE' },
        category: 'statistical',
    },
    NORMSDIST: {
        syntax: '(z)',
        description: { pt: 'Versão de compatibilidade de DIST.NORMP', fr: 'Version de compatibilité de LOI.NORMALE.STANDARD' },
        category: 'statistical',
    },
    NORMSINV: {
        syntax: '(probabilidade)',
        description: { pt: 'Versão de compatibilidade de INV.NORMP', fr: 'Version de compatibilité de LOI.NORMALE.STANDARD.INVERSE' },
        category: 'statistical',
    },
    PEARSON: {
        syntax: '(matriz1; matriz2)',
        description: { pt: 'Coeficiente de correlação de Pearson', fr: 'Coefficient de corrélation de Pearson' },
        category: 'statistical',
    },
    PHI: {
        syntax: '(x)',
        description: { pt: 'Valor da função de densidade normal padrão', fr: 'Valeur de la densité normale standard' },
        category: 'statistical',
    },
    POISSON: {
        syntax: '(x; média; cumulativo)',
        description: { pt: 'Versão de compatibilidade de POISSON', fr: 'Version de compatibilité de LOI.POISSON' },
        category: 'statistical',
    },
    'POISSON.DIST': {
        syntax: '(x; média; cumulativo)',
        description: { pt: 'Devolve a distribuição de Poisson', fr: 'Renvoie la distribution de Poisson' },
        category: 'statistical',
    },
    RSQ: {
        syntax: '(matriz_y; matriz_x)',
        description: { pt: 'Devolve o quadrado de r de Pearson', fr: 'Renvoie le carré de r de Pearson' },
        category: 'statistical',
    },
    SKEW: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Devolve a assimetria de uma distribuição', fr: 'Renvoie l\'asymétrie d\'une distribution' },
        category: 'statistical',
    },
    'SKEW.P': {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Assimetria com base na população', fr: 'Asymétrie basée sur la population' },
        category: 'statistical',
    },
    SLOPE: {
        syntax: '(matriz_y; matriz_x)',
        description: { pt: 'Declive da linha de regressão linear', fr: 'Pente de la droite de régression linéaire' },
        category: 'statistical',
    },
    SMALL: {
        syntax: '(matriz; k)',
        description: { pt: 'Devolve o k-ésimo menor valor', fr: 'Renvoie la k-ième plus petite valeur' },
        category: 'statistical',
    },
    STANDARDIZE: {
        syntax: '(x; média; desvio_padrão)',
        description: { pt: 'Devolve um valor normalizado', fr: 'Renvoie une valeur normalisée' },
        category: 'statistical',
    },
    STDEV: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Desvio padrão da amostra (compatibilidade)', fr: 'Écart type de l\'échantillon (compatibilité)' },
        category: 'statistical',
    },
    'STDEV.P': {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Desvio padrão da população', fr: 'Écart type de la population' },
        category: 'statistical',
    },
    'STDEV.S': {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Desvio padrão da amostra', fr: 'Écart type de l\'échantillon' },
        category: 'statistical',
    },
    STDEVA: {
        syntax: '(valor1; [valor2]; ...)',
        description: { pt: 'Desvio padrão incluindo texto e lógicos', fr: 'Écart type incluant texte et logiques' },
        category: 'statistical',
    },
    STDEVP: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Versão de compatibilidade de DESVPAD.P', fr: 'Version de compatibilité de ECARTYPE.PEARSON' },
        category: 'statistical',
    },
    STDEVPA: {
        syntax: '(valor1; [valor2]; ...)',
        description: { pt: 'Desvio padrão da população incluindo texto', fr: 'Écart type de la population incluant le texte' },
        category: 'statistical',
    },
    STEYX: {
        syntax: '(matriz_y; matriz_x)',
        description: { pt: 'Erro padrão da regressão', fr: 'Erreur type de la régression' },
        category: 'statistical',
    },
    'T.DIST': {
        syntax: '(x; graus_liberdade; cumulativo)',
        description: { pt: 'Devolve a distribuição t de Student', fr: 'Renvoie la distribution t de Student' },
        category: 'statistical',
    },
    'T.DIST.2T': {
        syntax: '(x; graus_liberdade)',
        description: { pt: 'Distribuição t de Student bicaudal', fr: 'Distribution t de Student bilatérale' },
        category: 'statistical',
    },
    'T.DIST.RT': {
        syntax: '(x; graus_liberdade)',
        description: { pt: 'Distribuição t de Student à direita', fr: 'Distribution t de Student à droite' },
        category: 'statistical',
    },
    'T.INV': {
        syntax: '(probabilidade; graus_liberdade)',
        description: { pt: 'Inverso da distribuição t de Student', fr: 'Inverse de la distribution t de Student' },
        category: 'statistical',
    },
    'T.INV.2T': {
        syntax: '(probabilidade; graus_liberdade)',
        description: { pt: 'Inverso bicaudal da distribuição t', fr: 'Inverse bilatéral de la distribution t' },
        category: 'statistical',
    },
    'T.TEST': {
        syntax: '(matriz1; matriz2; caudas; tipo)',
        description: { pt: 'Devolve a probabilidade do teste t', fr: 'Renvoie la probabilité du test t' },
        category: 'statistical',
    },
    TDIST: {
        syntax: '(x; graus_liberdade; caudas)',
        description: { pt: 'Versão de compatibilidade de DIST.T', fr: 'Version de compatibilité de LOI.STUDENT' },
        category: 'statistical',
    },
    TINV: {
        syntax: '(probabilidade; graus_liberdade)',
        description: { pt: 'Versão de compatibilidade de INV.T', fr: 'Version de compatibilité de LOI.STUDENT.INVERSE' },
        category: 'statistical',
    },
    TTEST: {
        syntax: '(matriz1; matriz2; caudas; tipo)',
        description: { pt: 'Versão de compatibilidade de TESTE.T', fr: 'Version de compatibilité de TEST.STUDENT' },
        category: 'statistical',
    },
    VAR: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Variância da amostra (compatibilidade)', fr: 'Variance de l\'échantillon (compatibilité)' },
        category: 'statistical',
    },
    'VAR.P': {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Variância da população', fr: 'Variance de la population' },
        category: 'statistical',
    },
    'VAR.S': {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Variância da amostra', fr: 'Variance de l\'échantillon' },
        category: 'statistical',
    },
    VARA: {
        syntax: '(valor1; [valor2]; ...)',
        description: { pt: 'Variância da amostra incluindo texto', fr: 'Variance de l\'échantillon incluant le texte' },
        category: 'statistical',
    },
    VARP: {
        syntax: '(num1; [num2]; ...)',
        description: { pt: 'Versão de compatibilidade de VAR.P', fr: 'Version de compatibilité de VAR.P' },
        category: 'statistical',
    },
    VARPA: {
        syntax: '(valor1; [valor2]; ...)',
        description: { pt: 'Variância da população incluindo texto', fr: 'Variance de la population incluant le texte' },
        category: 'statistical',
    },
    WEIBULL: {
        syntax: '(x; alfa; beta; cumulativo)',
        description: { pt: 'Versão de compatibilidade de DIST.WEIBULL', fr: 'Version de compatibilité de LOI.WEIBULL' },
        category: 'statistical',
    },
    'WEIBULL.DIST': {
        syntax: '(x; alfa; beta; cumulativo)',
        description: { pt: 'Devolve a distribuição de Weibull', fr: 'Renvoie la distribution de Weibull' },
        category: 'statistical',
    },
    'Z.TEST': {
        syntax: '(matriz; x; [sigma])',
        description: { pt: 'Devolve o valor P do teste Z', fr: 'Renvoie la valeur P du test Z' },
        category: 'statistical',
    },
    ZTEST: {
        syntax: '(matriz; x; [sigma])',
        description: { pt: 'Versão de compatibilidade de TESTE.Z', fr: 'Version de compatibilité de TEST.Z' },
        category: 'statistical',
    },

    // ==================== FINANCIAL ====================
    CUMIPMT: {
        syntax: '(taxa; nper; vp; per_inicial; per_final; tipo)',
        description: { pt: 'Juros acumulados entre dois períodos', fr: 'Intérêts cumulés entre deux périodes' },
        category: 'financial',
    },
    CUMPRINC: {
        syntax: '(taxa; nper; vp; per_inicial; per_final; tipo)',
        description: { pt: 'Capital acumulado entre dois períodos', fr: 'Capital cumulé entre deux périodes' },
        category: 'financial',
    },
    DB: {
        syntax: '(custo; residual; vida; período; [mês])',
        description: { pt: 'Depreciação pelo método de saldo fixo', fr: 'Amortissement par la méthode du solde fixe' },
        category: 'financial',
    },
    DDB: {
        syntax: '(custo; residual; vida; período; [fator])',
        description: { pt: 'Depreciação pelo método de saldo duplo', fr: 'Amortissement par la méthode du double solde' },
        category: 'financial',
    },
    DOLLARDE: {
        syntax: '(dólar_fracionário; fração)',
        description: { pt: 'Converte preço fracionário em decimal', fr: 'Convertit un prix fractionnaire en décimal' },
        category: 'financial',
    },
    DOLLARFR: {
        syntax: '(dólar_decimal; fração)',
        description: { pt: 'Converte preço decimal em fracionário', fr: 'Convertit un prix décimal en fractionnaire' },
        category: 'financial',
    },
    EFFECT: {
        syntax: '(taxa_nominal; nper_ano)',
        description: { pt: 'Devolve a taxa de juros efetiva anual', fr: 'Renvoie le taux d\'intérêt effectif annuel' },
        category: 'financial',
    },
    FV: {
        syntax: '(taxa; nper; pgto; [vp]; [tipo])',
        description: { pt: 'Devolve o valor futuro de um investimento', fr: 'Renvoie la valeur future d\'un investissement' },
        category: 'financial',
    },
    FVSCHEDULE: {
        syntax: '(capital; taxas)',
        description: { pt: 'Valor futuro com taxas de juro variáveis', fr: 'Valeur future avec taux d\'intérêt variables' },
        category: 'financial',
    },
    IPMT: {
        syntax: '(taxa; período; nper; vp; [vf]; [tipo])',
        description: { pt: 'Juros de um período de um investimento', fr: 'Intérêts d\'une période d\'un investissement' },
        category: 'financial',
    },
    ISPMT: {
        syntax: '(taxa; período; nper; vp)',
        description: { pt: 'Juros pagos num período específico', fr: 'Intérêts payés à une période spécifique' },
        category: 'financial',
    },
    MIRR: {
        syntax: '(valores; taxa_financiamento; taxa_reinvestimento)',
        description: { pt: 'Taxa interna de retorno modificada', fr: 'Taux de rendement interne modifié' },
        category: 'financial',
    },
    NOMINAL: {
        syntax: '(taxa_efetiva; nper_ano)',
        description: { pt: 'Devolve a taxa de juros nominal anual', fr: 'Renvoie le taux d\'intérêt nominal annuel' },
        category: 'financial',
    },
    NPER: {
        syntax: '(taxa; pgto; vp; [vf]; [tipo])',
        description: { pt: 'Número de períodos de um investimento', fr: 'Nombre de périodes d\'un investissement' },
        category: 'financial',
    },
    NPV: {
        syntax: '(taxa; valor1; [valor2]; ...)',
        description: { pt: 'Valor atual líquido de um investimento', fr: 'Valeur actuelle nette d\'un investissement' },
        category: 'financial',
    },
    PDURATION: {
        syntax: '(taxa; vp; vf)',
        description: { pt: 'Períodos para atingir um valor futuro', fr: 'Périodes pour atteindre une valeur future' },
        category: 'financial',
    },
    PMT: {
        syntax: '(taxa; nper; vp; [vf]; [tipo])',
        description: { pt: 'Pagamento periódico de um empréstimo', fr: 'Paiement périodique d\'un emprunt' },
        category: 'financial',
    },
    PPMT: {
        syntax: '(taxa; período; nper; vp; [vf]; [tipo])',
        description: { pt: 'Capital de um período de um empréstimo', fr: 'Capital d\'une période d\'un emprunt' },
        category: 'financial',
    },
    PV: {
        syntax: '(taxa; nper; pgto; [vf]; [tipo])',
        description: { pt: 'Devolve o valor presente de um investimento', fr: 'Renvoie la valeur actuelle d\'un investissement' },
        category: 'financial',
    },
    RATE: {
        syntax: '(nper; pgto; vp; [vf]; [tipo]; [estimativa])',
        description: { pt: 'Taxa de juros por período', fr: 'Taux d\'intérêt par période' },
        category: 'financial',
    },
    RRI: {
        syntax: '(nper; vp; vf)',
        description: { pt: 'Taxa de retorno equivalente', fr: 'Taux de rendement équivalent' },
        category: 'financial',
    },
    SLN: {
        syntax: '(custo; residual; vida)',
        description: { pt: 'Depreciação linear de um ativo', fr: 'Amortissement linéaire d\'un actif' },
        category: 'financial',
    },
    SYD: {
        syntax: '(custo; residual; vida; período)',
        description: { pt: 'Depreciação pela soma dos dígitos dos anos', fr: 'Amortissement par la somme des chiffres des années' },
        category: 'financial',
    },
    TBILLEQ: {
        syntax: '(liquidação; vencimento; desconto)',
        description: { pt: 'Rendimento equivalente de bilhete do tesouro', fr: 'Rendement équivalent d\'un bon du Trésor' },
        category: 'financial',
    },
    TBILLPRICE: {
        syntax: '(liquidação; vencimento; desconto)',
        description: { pt: 'Preço de bilhete do tesouro', fr: 'Prix d\'un bon du Trésor' },
        category: 'financial',
    },
    TBILLYIELD: {
        syntax: '(liquidação; vencimento; preço)',
        description: { pt: 'Rendimento de bilhete do tesouro', fr: 'Rendement d\'un bon du Trésor' },
        category: 'financial',
    },
    XNPV: {
        syntax: '(taxa; valores; datas)',
        description: { pt: 'Valor atual líquido com datas irregulares', fr: 'Valeur actuelle nette avec dates irrégulières' },
        category: 'financial',
    },

    // ==================== LOOKUP ====================
    ADDRESS: {
        syntax: '(linha; coluna; [tipo_abs]; [a1]; [folha])',
        description: { pt: 'Cria uma referência de célula como texto', fr: 'Crée une référence de cellule sous forme de texte' },
        category: 'lookup',
    },
    CHOOSE: {
        syntax: '(índice; valor1; [valor2]; ...)',
        description: { pt: 'Escolhe um valor de uma lista', fr: 'Choisit une valeur dans une liste' },
        category: 'lookup',
    },
    COLUMN: {
        syntax: '([ref])',
        description: { pt: 'Devolve o número da coluna de uma referência', fr: 'Renvoie le numéro de colonne d\'une référence' },
        category: 'lookup',
    },
    COLUMNS: {
        syntax: '(matriz)',
        description: { pt: 'Devolve o número de colunas de uma matriz', fr: 'Renvoie le nombre de colonnes d\'une matrice' },
        category: 'lookup',
    },
    FILTER: {
        syntax: '(matriz; incluir; [se_vazio])',
        description: { pt: 'Filtra um intervalo com base num critério', fr: 'Filtre une plage selon un critère' },
        category: 'lookup',
    },
    HLOOKUP: {
        syntax: '(valor; tabela; linha_índice; [intervalo])',
        description: { pt: 'Procura na primeira linha e devolve valor', fr: 'Recherche dans la première ligne et renvoie valeur' },
        category: 'lookup',
    },
    INDEX: {
        syntax: '(matriz; num_linha; [num_coluna])',
        description: { pt: 'Devolve valor de uma posição na matriz', fr: 'Renvoie la valeur d\'une position dans la matrice' },
        category: 'lookup',
    },
    MATCH: {
        syntax: '(valor_procurado; matriz; [tipo_correspondência])',
        description: { pt: 'Procura um valor e devolve a posição', fr: 'Recherche une valeur et renvoie la position' },
        category: 'lookup',
    },
    OFFSET: {
        syntax: '(ref; linhas; colunas; [altura]; [largura])',
        description: { pt: 'Devolve referência deslocada de uma célula', fr: 'Renvoie une référence décalée d\'une cellule' },
        category: 'lookup',
    },
    ROW: {
        syntax: '([ref])',
        description: { pt: 'Devolve o número da linha de uma referência', fr: 'Renvoie le numéro de ligne d\'une référence' },
        category: 'lookup',
    },
    ROWS: {
        syntax: '(matriz)',
        description: { pt: 'Devolve o número de linhas de uma matriz', fr: 'Renvoie le nombre de lignes d\'une matrice' },
        category: 'lookup',
    },
    SHEET: {
        syntax: '([valor])',
        description: { pt: 'Devolve o número da folha', fr: 'Renvoie le numéro de la feuille' },
        category: 'lookup',
    },
    SHEETS: {
        syntax: '([ref])',
        description: { pt: 'Devolve o número de folhas', fr: 'Renvoie le nombre de feuilles' },
        category: 'lookup',
    },
    TRANSPOSE: {
        syntax: '(matriz)',
        description: { pt: 'Transpõe linhas e colunas de uma matriz', fr: 'Transpose les lignes et colonnes d\'une matrice' },
        category: 'lookup',
    },
    VLOOKUP: {
        syntax: '(valor; tabela; col_índice; [intervalo])',
        description: { pt: 'Procura na primeira coluna e devolve valor', fr: 'Recherche dans la première colonne et renvoie valeur' },
        category: 'lookup',
    },
    XLOOKUP: {
        syntax: '(valor; matriz_procura; matriz_retorno; [se_não]; [modo]; [pesquisa])',
        description: { pt: 'Procura num intervalo e devolve correspondência', fr: 'Recherche dans une plage et renvoie correspondance' },
        category: 'lookup',
    },

    // ==================== ENGINEERING ====================
    BESSELI: {
        syntax: '(x; n)',
        description: { pt: 'Função de Bessel In(x) modificada', fr: 'Fonction de Bessel In(x) modifiée' },
        category: 'engineering',
    },
    BESSELJ: {
        syntax: '(x; n)',
        description: { pt: 'Função de Bessel Jn(x)', fr: 'Fonction de Bessel Jn(x)' },
        category: 'engineering',
    },
    BESSELK: {
        syntax: '(x; n)',
        description: { pt: 'Função de Bessel Kn(x) modificada', fr: 'Fonction de Bessel Kn(x) modifiée' },
        category: 'engineering',
    },
    BESSELY: {
        syntax: '(x; n)',
        description: { pt: 'Função de Bessel Yn(x)', fr: 'Fonction de Bessel Yn(x)' },
        category: 'engineering',
    },
    BIN2DEC: {
        syntax: '(num)',
        description: { pt: 'Converte binário em decimal', fr: 'Convertit binaire en décimal' },
        category: 'engineering',
    },
    BIN2HEX: {
        syntax: '(num; [posições])',
        description: { pt: 'Converte binário em hexadecimal', fr: 'Convertit binaire en hexadécimal' },
        category: 'engineering',
    },
    BIN2OCT: {
        syntax: '(num; [posições])',
        description: { pt: 'Converte binário em octal', fr: 'Convertit binaire en octal' },
        category: 'engineering',
    },
    BITAND: {
        syntax: '(num1; num2)',
        description: { pt: 'Devolve o E binário de dois números', fr: 'Renvoie le ET binaire de deux nombres' },
        category: 'engineering',
    },
    BITLSHIFT: {
        syntax: '(num; quantidade)',
        description: { pt: 'Deslocamento de bits à esquerda', fr: 'Décalage de bits vers la gauche' },
        category: 'engineering',
    },
    BITOR: {
        syntax: '(num1; num2)',
        description: { pt: 'Devolve o OU binário de dois números', fr: 'Renvoie le OU binaire de deux nombres' },
        category: 'engineering',
    },
    BITRSHIFT: {
        syntax: '(num; quantidade)',
        description: { pt: 'Deslocamento de bits à direita', fr: 'Décalage de bits vers la droite' },
        category: 'engineering',
    },
    BITXOR: {
        syntax: '(num1; num2)',
        description: { pt: 'Devolve o OU exclusivo binário', fr: 'Renvoie le OU exclusif binaire' },
        category: 'engineering',
    },
    COMPLEX: {
        syntax: '(real; imaginário; [sufixo])',
        description: { pt: 'Cria um número complexo', fr: 'Crée un nombre complexe' },
        category: 'engineering',
    },
    DEC2BIN: {
        syntax: '(num; [posições])',
        description: { pt: 'Converte decimal em binário', fr: 'Convertit décimal en binaire' },
        category: 'engineering',
    },
    DEC2HEX: {
        syntax: '(num; [posições])',
        description: { pt: 'Converte decimal em hexadecimal', fr: 'Convertit décimal en hexadécimal' },
        category: 'engineering',
    },
    DEC2OCT: {
        syntax: '(num; [posições])',
        description: { pt: 'Converte decimal em octal', fr: 'Convertit décimal en octal' },
        category: 'engineering',
    },
    DELTA: {
        syntax: '(num1; [num2])',
        description: { pt: 'Testa se dois valores são iguais', fr: 'Teste si deux valeurs sont égales' },
        category: 'engineering',
    },
    ERF: {
        syntax: '(limite_inferior; [limite_superior])',
        description: { pt: 'Devolve a função de erro', fr: 'Renvoie la fonction d\'erreur' },
        category: 'engineering',
    },
    ERFC: {
        syntax: '(x)',
        description: { pt: 'Devolve a função de erro complementar', fr: 'Renvoie la fonction d\'erreur complémentaire' },
        category: 'engineering',
    },
    HEX2BIN: {
        syntax: '(num; [posições])',
        description: { pt: 'Converte hexadecimal em binário', fr: 'Convertit hexadécimal en binaire' },
        category: 'engineering',
    },
    HEX2DEC: {
        syntax: '(num)',
        description: { pt: 'Converte hexadecimal em decimal', fr: 'Convertit hexadécimal en décimal' },
        category: 'engineering',
    },
    HEX2OCT: {
        syntax: '(num; [posições])',
        description: { pt: 'Converte hexadecimal em octal', fr: 'Convertit hexadécimal en octal' },
        category: 'engineering',
    },
    IMABS: {
        syntax: '(inúmero)',
        description: { pt: 'Valor absoluto de número complexo', fr: 'Valeur absolue d\'un nombre complexe' },
        category: 'engineering',
    },
    IMAGINARY: {
        syntax: '(inúmero)',
        description: { pt: 'Parte imaginária de número complexo', fr: 'Partie imaginaire d\'un nombre complexe' },
        category: 'engineering',
    },
    IMARGUMENT: {
        syntax: '(inúmero)',
        description: { pt: 'Argumento (ângulo) de número complexo', fr: 'Argument (angle) d\'un nombre complexe' },
        category: 'engineering',
    },
    IMCONJUGATE: {
        syntax: '(inúmero)',
        description: { pt: 'Conjugado de número complexo', fr: 'Conjugué d\'un nombre complexe' },
        category: 'engineering',
    },
    IMCOS: {
        syntax: '(inúmero)',
        description: { pt: 'Cosseno de número complexo', fr: 'Cosinus d\'un nombre complexe' },
        category: 'engineering',
    },
    IMCOSH: {
        syntax: '(inúmero)',
        description: { pt: 'Cosseno hiperbólico de número complexo', fr: 'Cosinus hyperbolique d\'un nombre complexe' },
        category: 'engineering',
    },
    IMCOT: {
        syntax: '(inúmero)',
        description: { pt: 'Cotangente de número complexo', fr: 'Cotangente d\'un nombre complexe' },
        category: 'engineering',
    },
    IMCSC: {
        syntax: '(inúmero)',
        description: { pt: 'Cossecante de número complexo', fr: 'Cosécante d\'un nombre complexe' },
        category: 'engineering',
    },
    IMCSCH: {
        syntax: '(inúmero)',
        description: { pt: 'Cossecante hiperbólica de número complexo', fr: 'Cosécante hyperbolique d\'un nombre complexe' },
        category: 'engineering',
    },
    IMDIV: {
        syntax: '(inúmero1; inúmero2)',
        description: { pt: 'Divisão de números complexos', fr: 'Division de nombres complexes' },
        category: 'engineering',
    },
    IMEXP: {
        syntax: '(inúmero)',
        description: { pt: 'Exponencial de número complexo', fr: 'Exponentielle d\'un nombre complexe' },
        category: 'engineering',
    },
    IMLN: {
        syntax: '(inúmero)',
        description: { pt: 'Logaritmo natural de número complexo', fr: 'Logarithme naturel d\'un nombre complexe' },
        category: 'engineering',
    },
    IMLOG10: {
        syntax: '(inúmero)',
        description: { pt: 'Logaritmo base 10 de número complexo', fr: 'Logarithme en base 10 d\'un nombre complexe' },
        category: 'engineering',
    },
    IMLOG2: {
        syntax: '(inúmero)',
        description: { pt: 'Logaritmo base 2 de número complexo', fr: 'Logarithme en base 2 d\'un nombre complexe' },
        category: 'engineering',
    },
    IMPOWER: {
        syntax: '(inúmero; potência)',
        description: { pt: 'Número complexo elevado a uma potência', fr: 'Nombre complexe élevé à une puissance' },
        category: 'engineering',
    },
    IMPRODUCT: {
        syntax: '(inúmero1; [inúmero2]; ...)',
        description: { pt: 'Produto de números complexos', fr: 'Produit de nombres complexes' },
        category: 'engineering',
    },
    IMREAL: {
        syntax: '(inúmero)',
        description: { pt: 'Parte real de número complexo', fr: 'Partie réelle d\'un nombre complexe' },
        category: 'engineering',
    },
    IMSEC: {
        syntax: '(inúmero)',
        description: { pt: 'Secante de número complexo', fr: 'Sécante d\'un nombre complexe' },
        category: 'engineering',
    },
    IMSECH: {
        syntax: '(inúmero)',
        description: { pt: 'Secante hiperbólica de número complexo', fr: 'Sécante hyperbolique d\'un nombre complexe' },
        category: 'engineering',
    },
    IMSIN: {
        syntax: '(inúmero)',
        description: { pt: 'Seno de número complexo', fr: 'Sinus d\'un nombre complexe' },
        category: 'engineering',
    },
    IMSINH: {
        syntax: '(inúmero)',
        description: { pt: 'Seno hiperbólico de número complexo', fr: 'Sinus hyperbolique d\'un nombre complexe' },
        category: 'engineering',
    },
    IMSQRT: {
        syntax: '(inúmero)',
        description: { pt: 'Raiz quadrada de número complexo', fr: 'Racine carrée d\'un nombre complexe' },
        category: 'engineering',
    },
    IMSUB: {
        syntax: '(inúmero1; inúmero2)',
        description: { pt: 'Subtração de números complexos', fr: 'Soustraction de nombres complexes' },
        category: 'engineering',
    },
    IMSUM: {
        syntax: '(inúmero1; [inúmero2]; ...)',
        description: { pt: 'Soma de números complexos', fr: 'Somme de nombres complexes' },
        category: 'engineering',
    },
    IMTAN: {
        syntax: '(inúmero)',
        description: { pt: 'Tangente de número complexo', fr: 'Tangente d\'un nombre complexe' },
        category: 'engineering',
    },
    OCT2BIN: {
        syntax: '(num; [posições])',
        description: { pt: 'Converte octal em binário', fr: 'Convertit octal en binaire' },
        category: 'engineering',
    },
    OCT2DEC: {
        syntax: '(num)',
        description: { pt: 'Converte octal em decimal', fr: 'Convertit octal en décimal' },
        category: 'engineering',
    },
    OCT2HEX: {
        syntax: '(num; [posições])',
        description: { pt: 'Converte octal em hexadecimal', fr: 'Convertit octal en hexadécimal' },
        category: 'engineering',
    },

    // ==================== INFORMATION ====================
    ISBLANK: {
        syntax: '(valor)',
        description: { pt: 'Verifica se o valor está vazio', fr: 'Vérifie si la valeur est vide' },
        category: 'information',
    },
    ISERR: {
        syntax: '(valor)',
        description: { pt: 'Verifica se é erro (exceto #N/D)', fr: 'Vérifie si c\'est une erreur (sauf #N/A)' },
        category: 'information',
    },
    ISERROR: {
        syntax: '(valor)',
        description: { pt: 'Verifica se o valor é um erro', fr: 'Vérifie si la valeur est une erreur' },
        category: 'information',
    },
    ISEVEN: {
        syntax: '(num)',
        description: { pt: 'Verifica se o número é par', fr: 'Vérifie si le nombre est pair' },
        category: 'information',
    },
    ISFORMULA: {
        syntax: '(ref)',
        description: { pt: 'Verifica se a célula contém fórmula', fr: 'Vérifie si la cellule contient une formule' },
        category: 'information',
    },
    ISLOGICAL: {
        syntax: '(valor)',
        description: { pt: 'Verifica se o valor é lógico', fr: 'Vérifie si la valeur est logique' },
        category: 'information',
    },
    ISNA: {
        syntax: '(valor)',
        description: { pt: 'Verifica se o valor é #N/D', fr: 'Vérifie si la valeur est #N/A' },
        category: 'information',
    },
    ISNONTEXT: {
        syntax: '(valor)',
        description: { pt: 'Verifica se o valor não é texto', fr: 'Vérifie si la valeur n\'est pas du texte' },
        category: 'information',
    },
    ISNUMBER: {
        syntax: '(valor)',
        description: { pt: 'Verifica se o valor é um número', fr: 'Vérifie si la valeur est un nombre' },
        category: 'information',
    },
    ISODD: {
        syntax: '(num)',
        description: { pt: 'Verifica se o número é ímpar', fr: 'Vérifie si le nombre est impair' },
        category: 'information',
    },
    ISREF: {
        syntax: '(valor)',
        description: { pt: 'Verifica se o valor é uma referência', fr: 'Vérifie si la valeur est une référence' },
        category: 'information',
    },
    ISTEXT: {
        syntax: '(valor)',
        description: { pt: 'Verifica se o valor é texto', fr: 'Vérifie si la valeur est du texte' },
        category: 'information',
    },
    NA: {
        syntax: '()',
        description: { pt: 'Devolve o valor de erro #N/D', fr: 'Renvoie la valeur d\'erreur #N/A' },
        category: 'information',
    },

    // ==================== MATRIX ====================
    MMULT: {
        syntax: '(matriz1; matriz2)',
        description: { pt: 'Devolve o produto de duas matrizes', fr: 'Renvoie le produit de deux matrices' },
        category: 'matrix',
    },
};

export const hfOnlyFunctions: Set<string> = new Set([
    'HF.ADD', 'HF.CONCAT', 'HF.DIVIDE', 'HF.EQ', 'HF.GT', 'HF.GTE',
    'HF.LT', 'HF.LTE', 'HF.MINUS', 'HF.MULTIPLY', 'HF.NE', 'HF.POW',
    'HF.UMINUS', 'HF.UNARY_PERCENT', 'HF.UPLUS',
    'ARRAY_CONSTRAIN', 'ARRAYFORMULA', 'COUNTUNIQUE', 'SPLIT',
    'MAXPOOL', 'MEDIANPOOL', 'ISBINARY', 'INTERVAL',
]);

export const legacyAliases: Set<string> = new Set([
    'WEIBULLDIST', 'VARS', 'TINV2T', 'TDISTRT', 'TDIST2T', 'STDEVS',
    'FINVRT', 'FDISTRT', 'CHIDISTRT', 'CHIINVRT', 'COVARIANCEP',
    'COVARIANCES', 'LOGNORMINV', 'POISSONDIST', 'SKEWP',
    'CEILING.PRECISE', 'FLOOR.PRECISE', 'ISO.CEILING',
]);
